// English source strings for visitor-type workflows (module 07 remainders, ADR-027): protocol for
// restricted visitor types, recruitment candidates, maintenance work orders, courier deliveries and the
// restricted-area credential rule. Arabic: ar-workflows.ts. Wording keeps facts independent: nothing
// here says a protocol step, sponsor, work order or handover approves, admits or releases anyone.
export const enWorkflows = {
  'specialist.hr-assessor': 'HR assessor (HR-only candidate notes, HR sponsor)',

  // ------------------------------------------------------------ API messages
  'vtw.api.assistedRecorded': 'The assisted arrival is already recorded for this visit.',
  'vtw.api.notCandidateType': 'This visitor type has no candidate handling.',
  'vtw.api.notDeliveryType': 'This visitor type is not a delivery workflow.',
  'vtw.api.alreadyLinked': 'This visitor or this delivery is already linked.',
  'vtw.api.deliveryCompleted': 'This delivery participation is already completed.',
  'vtw.api.handoverPending': 'Record the handover or refusal on the delivery desk first.',
  'vtw.v.handoverAreaRequired': 'Visitors of this type go only to an open delivery handover area. Choose one as the destination.',
  'vtw.v.hrSponsorRequired': 'Name an HR sponsor who holds the HR assessor role at this site before submitting.',
  'vtw.v.workOrderRequired': 'Bind this visit to a work order before submitting.',
  'vtw.v.workOrderNotCovering': 'The bound work order or permit does not cover this visit. Review the reasons in the work order section.',
  'vtw.v.notProtocolOffice': 'Choose someone in the protocol office at this site.',
  'vtw.v.notHrSponsor': 'Choose someone who holds the HR assessor role at this site.',
  'vtw.v.deliveryAreaMismatch': 'This delivery is at a different handover area from the visit destination.',
  'vtw.v.waiveReason': 'Give the reason for waiving this step.',
  'vtw.v.purposeRequired': 'State why you need to read these notes.',

  // ------------------------------------------------------------ credential blockers (PB-07-14, PB-07-06)
  'cred.blocker.restricted_zone_not_authorised': 'A restricted zone is selected but this visitor has no approved restricted-area authorisation.',
  'cred.blocker.restricted_zone_identity': 'A restricted zone needs current identity evidence valid until the credential ends.',
  'cred.blocker.restricted_zone_escort': 'A restricted zone needs an escort assigned to the visit.',
  'cred.blocker.work_order_not_covering': 'The bound work order or permit no longer covers this visit.',

  // ------------------------------------------------------------ notifications
  'comms.key.protocol_notice': 'Protocol notice to the protocol contact and owner',
  'vpolicy.ntf.t.protocol_notice': 'Protocol notice',

  // ------------------------------------------------------------ visit panel
  'vtw.title': 'Visitor-type handling',
  'vtw.intro': 'Configured for this visitor type. These facts do not approve, admit or give access by themselves.',
  'vtw.none': 'None',
  'vtw.choose': 'Choose…',
  'vtw.actions': 'Actions',

  'vtw.protocol.title': 'Protocol',
  'vtw.protocol.contact': 'Protocol contact',
  'vtw.protocol.owner': 'Protocol owner',
  'vtw.protocol.notices': 'Protocol notices',
  'vtw.protocol.noticesOn': 'Sent to the contact and owner',
  'vtw.protocol.noticesOff': 'Not sent for this visitor type',
  'vtw.protocol.route': 'Assisted route',
  'vtw.protocol.routeMissing': 'Required — not set yet',
  'vtw.protocol.routePoint': 'Meeting point',
  'vtw.protocol.routeNote': 'Route note',
  'vtw.protocol.routeNoteHint': 'Visible to reception so they can take the visitor in. Up to 300 characters.',
  'vtw.protocol.assistedAt': 'Met on the assisted route {when} by {name}',
  'vtw.protocol.recordAssisted': 'Record assisted arrival',
  'vtw.protocol.itinerary': 'Private itinerary',
  'vtw.protocol.itineraryRestricted': 'Recorded — visible only to the protocol office and the visit’s hosts',
  'vtw.protocol.itineraryHint': 'Only the protocol office and this visit’s hosts can read it. It is never sent in notices.',
  'vtw.protocol.factsHint': 'A VIP or official label never bypasses approval, identity or arrival checks.',
  'vtw.protocol.edit': 'Edit protocol',
  'vtw.protocol.editHint': 'The contact and owner must belong to the protocol office at this site.',
  'vtw.protocol.officeHint': 'Only people in the protocol office are listed.',
  'vtw.protocol.steps': 'Protocol steps',
  'vtw.protocol.stepsHint': { zero: 'No required step is outstanding.', one: '1 required step is outstanding.', other: '{count} required steps are outstanding.' },
  'vtw.protocol.step': 'Step',
  'vtw.protocol.status': 'Status',
  'vtw.protocol.recorded': 'Recorded',
  'vtw.protocol.required': 'Required',
  'vtw.protocol.optional': 'Optional',
  'vtw.protocol.version': 'policy version {n}',
  'vtw.protocol.complete': 'Mark completed',
  'vtw.protocol.waive': 'Waive',
  'vtw.protocol.stepNote': 'Note',
  'vtw.protocol.stepNoteHint': 'Optional. Up to 500 characters.',
  'vtw.protocol.waiveHint': 'Required: why this step is waived.',
  'vtw.step.pending': 'Pending',
  'vtw.step.completed': 'Completed',
  'vtw.step.waived': 'Waived',
  'vtw.step.superseded': 'Replaced by a later policy',

  'vtw.candidate.title': 'Candidate (HR)',
  'vtw.candidate.sponsor': 'HR sponsor',
  'vtw.candidate.sponsorMissing': 'Required — not named yet',
  'vtw.candidate.setSponsor': 'Set HR sponsor',
  'vtw.candidate.sponsorHint': 'Only people with the HR assessor role at this site are listed.',
  'vtw.candidate.notes': 'HR-only notes',
  'vtw.candidate.notesAvailable': 'You may read them for a stated purpose; each read is recorded.',
  'vtw.candidate.notesRestricted': 'Only HR assessors can read assessment notes.',
  'vtw.candidate.purpose': 'Purpose',
  'vtw.candidate.purposeHint': 'Each read is recorded in the audit trail with this purpose.',
  'vtw.candidate.show': 'Show notes',
  'vtw.candidate.noNotes': 'No notes yet.',
  'vtw.candidate.correction': 'correction',
  'vtw.candidate.redacted': 'Removed under the retention rules.',
  'vtw.candidate.addNote': 'Add an HR-only note',
  'vtw.candidate.addNoteHint': 'Notes cannot be edited; add a correction instead. Up to 4,000 characters.',
  'vtw.candidate.about': 'About',
  'vtw.candidate.aboutVisit': 'The whole visit',
  'vtw.candidate.saveNote': 'Save note',
  'vtw.purpose.hiring_decision': 'Hiring decision',
  'vtw.purpose.interview_panel': 'Interview panel preparation',
  'vtw.purpose.candidate_request': 'Candidate’s own request',
  'vtw.purpose.complaint_or_audit': 'Complaint or audit',

  'vtw.work.title': 'Work order',
  'vtw.work.required': 'This visitor type needs a work order that covers the visit.',
  'vtw.work.requiredWithPermit': 'This visitor type needs a work order and an approved permit that cover the visit.',
  'vtw.work.optional': 'A work order may be bound to this visit.',
  'vtw.work.unbound': 'No work order is bound yet.',
  'vtw.work.order': 'Work order',
  'vtw.work.permit': 'Permit',
  'vtw.work.permitHint': 'Approved permits at this site that are still valid.',
  'vtw.work.validUntil': 'valid until {when}',
  'vtw.work.coverage': 'Coverage',
  'vtw.work.evaluatedAt': 'checked {when}',
  'vtw.work.history': { one: 'Re-evaluation history (1)', other: 'Re-evaluation history ({count})' },
  'vtw.work.liveCredentials': { one: '1 credential still live', other: '{count} credentials still live' },
  'vtw.work.bind': 'Bind work order',
  'vtw.work.rebind': 'Change work order',
  'vtw.work.bindHint': 'Coverage is checked now and again after every change to the visit.',
  'vtw.coverage.covered': 'Covered',
  'vtw.coverage.not_covered': 'Not covered',
  'vtw.trigger.bound': 'bound',
  'vtw.trigger.submitted': 'submitted',
  'vtw.trigger.visit_changed': 'visit changed',
  'vtw.trigger.participants_changed': 'visitors changed',
  'vtw.block.no_schedule': 'The visit has no schedule yet.',
  'vtw.block.work_package_closed': 'The work order is closed.',
  'vtw.block.outside_work_package': 'The visit falls outside the work order dates.',
  'vtw.block.permit_required': 'An approved permit is required.',
  'vtw.block.permit_not_approved': 'The permit is not approved.',
  'vtw.block.permit_company': 'The permit belongs to another company.',
  'vtw.block.permit_work_package': 'The permit belongs to another work order.',
  'vtw.block.permit_zone': 'The permit does not cover the visit destination.',
  'vtw.block.permit_window': 'The permit does not cover the whole visit (for example after the end time was extended).',
  'vtw.block.participant_not_on_permit': 'A visitor is not named on the permit.',
  'vtw.permitStatus.draft': 'Draft',
  'vtw.permitStatus.submitted': 'Submitted',
  'vtw.permitStatus.approved': 'Approved',
  'vtw.permitStatus.rejected': 'Rejected',
  'vtw.permitStatus.invalidated': 'Invalidated',
  'vtw.permitStatus.withdrawn': 'Withdrawn',

  'vtw.delivery.title': 'Delivery',
  'vtw.delivery.destinationOk': 'The destination is an approved handover area.',
  'vtw.delivery.destinationMissing': 'The destination must be an open delivery handover area.',
  'vtw.delivery.none': 'No delivery is linked yet.',
  'vtw.delivery.courier': 'Courier',
  'vtw.delivery.packages': 'Packages',
  'vtw.delivery.handover': 'Handover',
  'vtw.delivery.presence': 'Presence',
  'vtw.delivery.vehicle': 'Vehicle',
  'vtw.delivery.record': 'Delivery record',
  'vtw.delivery.person': 'Person',
  'vtw.delivery.courierFact': 'Delivery desk',
  'vtw.delivery.packageCount': { one: '1 package', other: '{count} packages' },
  'vtw.delivery.recipient': 'For {name}',
  'vtw.delivery.completedBy': 'Completion recorded by {name}, {when}',
  'vtw.delivery.complete': 'Record completion',
  'vtw.delivery.link': 'Link a delivery',
  'vtw.delivery.linkHint': 'Link the courier to a delivery recorded on the delivery desk.',
  'vtw.delivery.independenceHint': 'Completing a handover never records that the courier or the vehicle has left; departures are recorded separately.',
  'vtw.package.pending': 'Waiting for handover',
  'vtw.package.handed_over': 'Handed over',
  'vtw.package.refused': 'Refused',
  'vtw.presence.no_observation': 'Not observed',
  'vtw.presence.observed_onsite': 'On site',
  'vtw.presence.observed_departed': 'Departed',
  'vtw.presence.unknown_conflicting': 'Unknown',

  // ------------------------------------------------------------ policy editor
  'vtw.policy.title': 'Visitor-type handling',
  'vtw.policy.hint': 'Optional behaviour for this visitor type. It is configuration: nothing depends on the category’s name.',
  'vtw.policy.protocol': 'Protocol (contact, owner, assisted route, steps)',
  'vtw.policy.candidate': 'Candidate (HR sponsor and HR-only notes)',
  'vtw.policy.workOrder': 'Work order (maintenance)',
  'vtw.policy.delivery': 'Delivery (handover area and delivery record)',
  'vtw.policy.contactHint': 'They must hold the protocol officer role where the visit takes place.',
  'vtw.policy.notify': 'Send protocol notices',
  'vtw.policy.assistedRoute': 'Assisted route',
  'vtw.policy.stepsHint': 'Steps beyond the approval stages. Completed steps are never rewritten by a later policy version.',
  'vtw.policy.stepKey': 'Step key',
  'vtw.policy.stepLabelEn': 'Label (English)',
  'vtw.policy.stepLabelAr': 'Label (Arabic)',
  'vtw.policy.addStep': 'Add step',
  'vtw.policy.removeStep': 'Remove step',
  'vtw.policy.hrSponsorRequired': 'An HR sponsor is required before submission',
  'vtw.policy.workOrderRequired': 'A covering work order is required before submission',
  'vtw.policy.permitRequired': 'An approved covering permit is required',

  'vtw.restricted.title': 'Restricted zones in credentials',
  'vtw.restricted.hint': 'A restricted zone is only included in a credential when the visitor has restricted-area approval and the evidence below. Nothing is widened to site-wide access.',
  'vtw.restricted.identity': 'Identity evidence',
  'vtw.restricted.identityRequired': 'Required, valid until the credential ends',
  'vtw.restricted.identityNotRequired': 'Not required',
  'vtw.restricted.escort': 'An escort must be assigned',
} as const;
